import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Clock, Star } from "lucide-react";

export default function SupplierTempoSettings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", value: "", unit: "Hari", active: true, is_default: false });

  const load = () => api.get("/supplier-tempos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || form.value === "") { toast.error("Nama dan nilai wajib diisi"); return; }
    try {
      await api.post("/supplier-tempos", { ...form, value: parseInt(form.value) });
      toast.success("Tempo ditambahkan");
      setOpen(false);
      setForm({ name: "", value: "", unit: "Hari", active: true, is_default: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/supplier-tempos/${id}`, updates); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "supplier_tempo", resource_id: id, label: "tempo supplier" });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Tempo dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Pengaturan Tempo Tagihan Supplier</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Kelola pilihan tempo (Hari / Bulan / Tahun) yang bisa dipakai Admin saat membuat order.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-tempo-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Tambah Tempo</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Tempo Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-1.5"><Label>Nama Tempo</Label><Input data-testid="tempo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth. 45 Hari / 3 Bulan" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nilai</Label><Input data-testid="tempo-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="0" /></div>
                <div className="space-y-1.5">
                  <Label>Satuan</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="tempo-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hari">Hari</SelectItem>
                      <SelectItem value="Bulan">Bulan</SelectItem>
                      <SelectItem value="Tahun">Tahun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <Label>Jadikan tempo default</Label>
                <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="tempo-default" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={submit} data-testid="tempo-submit" variant="gold">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama Tempo</TableHead>
                <TableHead>Nilai</TableHead>
                <TableHead>Satuan</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Aktif</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Belum ada tempo.</TableCell></TableRow>}
              {items.map((t) => (
                <TableRow key={t._id} data-testid={`tempo-row-${t._id}`}>
                  <TableCell className="font-medium flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" />{t.name}</TableCell>
                  <TableCell>{t.value}</TableCell>
                  <TableCell><Badge variant="outline">{t.unit}</Badge></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={() => patch(t._id, { is_default: true })} data-testid={`tempo-setdefault-${t._id}`}>
                      <Star className={`h-4 w-4 ${t.is_default ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                      {t.is_default ? "Default" : "Set"}
                    </Button>
                  </TableCell>
                  <TableCell><Switch checked={t.active} onCheckedChange={(v) => patch(t._id, { active: v })} data-testid={`tempo-active-${t._id}`} /></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(t._id)} data-testid={`tempo-delete-${t._id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {items.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Belum ada tempo.</p>}
          {items.map((t) => (
            <div key={t._id} data-testid={`tempo-row-${t._id}`} className="p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{t.name}</p>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{t.value} {t.unit}</Badge>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-1.5" onClick={() => patch(t._id, { is_default: true })} data-testid={`tempo-setdefault-${t._id}`}>
                    <Star className={`h-3.5 w-3.5 ${t.is_default ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                    {t.is_default ? "Default" : "Set"}
                  </Button>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">Aktif</span>
                    <Switch checked={t.active} onCheckedChange={(v) => patch(t._id, { active: v })} data-testid={`tempo-active-${t._id}`} />
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(t._id)} data-testid={`tempo-delete-${t._id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
